import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle2, AlertCircle, ArrowRight, X } from 'lucide-react';
import {
  supabase,
  isConfigured,
  storePendingMarketingConsent,
  checkRateLimits,
} from '../services/supabase';

interface LoginProps {
  onAuthSuccess: () => void;
  onClose?: () => void;
  initialMode?: 'login' | 'signup';
}

export default function Login({ onAuthSuccess, onClose, initialMode = 'login' }: LoginProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState('');

  const handleGoogle = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      return;
    }
    if (!isConfigured) {
      setError('Supabase is not configured.');
      return;
    }
    setGoogleLoading(true);
    setError(null);
    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!acceptedTerms) throw new Error('Please accept the Terms and Conditions to continue.');
      if (website.trim()) throw new Error('Something went wrong. Please try again.');
      if (!isConfigured) throw new Error('Supabase is not configured.');

      const limit = await checkRateLimits(email);
      if (!limit.allowed) throw new Error(limit.reason || 'Too many sign-in attempts. Please try later.');

      if (mode === 'signup' && acceptedMarketing) {
        await storePendingMarketingConsent(email);
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/`, data: { signup_app: 'valmate' } },
      });
      if (error) throw error;
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-full max-w-md mx-auto bg-slate-950/95 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl shadow-[#f47a6b]/20 overflow-hidden">
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-white p-1 rounded-lg z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}

      <div className="p-6 sm:p-8">
        <div className="text-center mb-6">
          <img src="/Logo.png" alt="Valmate" className="h-10 w-auto mx-auto mb-4" />
          <p className="text-sm text-slate-400">
            {mode === 'login'
              ? 'Welcome back.'
              : 'Create your agent account to start capturing valuation leads.'}
          </p>
        </div>

        <div className="flex gap-1 p-1 bg-slate-900/60 border border-slate-800 rounded-lg mb-5">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-2 text-sm font-medium rounded transition-colors ${
              mode === 'login' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Sign in
          </button>
          <button
            onClick={() => setMode('signup')}
            className={`flex-1 py-2 text-sm font-medium rounded transition-colors ${
              mode === 'signup' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Create account
          </button>
        </div>

        {sent ? (
          <div className="text-center py-8">
            <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: '#f47a6b' }} />
            <p className="text-lg font-semibold text-white mb-1">Check your inbox</p>
            <p className="text-sm text-slate-400">We sent a magic link to {email}.</p>
            <button
              onClick={onAuthSuccess}
              className="mt-6 text-xs text-slate-500 hover:text-[#f47a6b]"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleEmail} className="space-y-4">
            {/* honeypot */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@agency.co.uk"
                className="w-full px-3 py-2.5 bg-slate-900/80 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#f47a6b]"
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
                style={{ accentColor: '#f47a6b' }}
              />
              <span>
                I agree to the{' '}
                <a href="https://rehub.studio/terms" className="text-[#f47a6b] hover:underline">
                  Terms
                </a>{' '}
                and{' '}
                <a href="https://rehub.studio/privacy" className="text-[#f47a6b] hover:underline">
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            {mode === 'signup' && (
              <label className="flex items-start gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={acceptedMarketing}
                  onChange={(e) => setAcceptedMarketing(e.target.checked)}
                  className="mt-0.5"
                  style={{ accentColor: '#f47a6b' }}
                />
                <span>
                  Send me product updates and tips for capturing more valuation leads (no spam,
                  unsubscribe anytime).
                </span>
              </label>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-900/30 border border-rose-700/40 rounded-lg text-sm text-rose-200">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 disabled:opacity-50 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ background: '#f47a6b' }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#e56a5a')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#f47a6b')}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {mode === 'login' ? 'Send magic link' : 'Create account'}
              <ArrowRight size={14} />
            </button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-slate-950 text-slate-500">or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-50 rounded-lg text-sm font-semibold transition-colors"
            >
              {googleLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 48 48">
                  <path
                    fill="#FFC107"
                    d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.4 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.376 0-9.93-3.067-11.283-7.347l-6.522 5.025C9.504 39.556 16.227 44 24 44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
                  />
                </svg>
              )}
              Continue with Google
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
